import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('d2tally_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('d2tally_token');
      localStorage.removeItem('d2tally_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('d2tally_user');
    const token = localStorage.getItem('d2tally_token');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('d2tally_token', access_token);
    localStorage.setItem('d2tally_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'admin') => {
    const response = await api.post('/auth/register', { email, password, name, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('d2tally_token', access_token);
    localStorage.setItem('d2tally_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('d2tally_token');
    localStorage.removeItem('d2tally_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const apiService = {
  // Dashboard
  getDashboardStats: () => api.get('/dashboard/stats'),
  getRecentLogs: (limit = 10) => api.get(`/dashboard/recent-logs?limit=${limit}`),
  getAlerts: () => api.get('/dashboard/alerts'),

  // Tally Connections
  getTallyConnections: () => api.get('/tally-connections'),
  createTallyConnection: (data) => api.post('/tally-connections', data),
  updateTallyConnection: (id, data) => api.put(`/tally-connections/${id}`, data),
  deleteTallyConnection: (id) => api.delete(`/tally-connections/${id}`),
  testTallyConnection: (id) => api.post(`/tally-connections/${id}/test`),

  // Database Connections (MySQL, MSSQL, MariaDB)
  getDBConnections: () => api.get('/db-connections'),
  createDBConnection: (data) => api.post('/db-connections', data),
  updateDBConnection: (id, data) => api.put(`/db-connections/${id}`, data),
  deleteDBConnection: (id) => api.delete(`/db-connections/${id}`),
  testDBConnection: (id) => api.post(`/db-connections/${id}/test`),

  // Datasets
  getDatasets: () => api.get('/datasets'),
  getDataset: (id) => api.get(`/datasets/${id}`),
  createDataset: (data) => api.post('/datasets', data),
  updateDataset: (id, data) => api.put(`/datasets/${id}`, data),
  deleteDataset: (id) => api.delete(`/datasets/${id}`),
  previewDataset: (id, limit = 10) => api.post(`/datasets/${id}/preview?limit=${limit}`),

  // XML Templates
  getXMLTemplates: () => api.get('/xml-templates'),
  getXMLTemplate: (id) => api.get(`/xml-templates/${id}`),
  createXMLTemplate: (data) => api.post('/xml-templates', data),
  updateXMLTemplate: (id, data) => api.put(`/xml-templates/${id}`, data),
  deleteXMLTemplate: (id) => api.delete(`/xml-templates/${id}`),

  // Mappings
  getMappings: () => api.get('/mappings'),
  getMapping: (id) => api.get(`/mappings/${id}`),
  createMapping: (data) => api.post('/mappings', data),
  updateMapping: (id, data) => api.put(`/mappings/${id}`, data),
  deleteMapping: (id) => api.delete(`/mappings/${id}`),

  // Jobs
  getJobs: () => api.get('/jobs'),
  getJob: (id) => api.get(`/jobs/${id}`),
  createJob: (data) => api.post('/jobs', data),
  updateJob: (id, data) => api.put(`/jobs/${id}`, data),
  deleteJob: (id) => api.delete(`/jobs/${id}`),
  runJob: (id, mode) => api.post(`/jobs/${id}/run${mode ? `?mode=${mode}` : ''}`),
  toggleJob: (id) => api.post(`/jobs/${id}/toggle`),
  getPostedRecords: (id) => api.get(`/jobs/${id}/posted-records`),
  previewJobXml: (id, mode) => api.post(`/jobs/${id}/preview-xml${mode ? `?mode=${mode}` : ''}`),
  reconcileJob: (id) => api.post(`/jobs/${id}/reconcile`),
  restoreDeleted: (id) => api.post(`/jobs/${id}/restore-deleted`),

  // Audit Logs
  getAuditLogs: (params = {}) => api.get('/logs', { params }),
  getAuditLog: (id) => api.get(`/logs/${id}`),

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  testSMTP: () => api.post('/settings/test-smtp'),

  // AI Chat
  sendAIMessage: (data) => api.post('/ai/chat', data),
  getChatHistory: (sessionId) => api.get(`/ai/chat/${sessionId}`),
  clearChatHistory: (sessionId) => api.delete(`/ai/chat/${sessionId}`),

  // Fetch Engine
  getFetchConfigs: () => api.get('/fetch/configs'),
  getFetchConfig: (id) => api.get(`/fetch/configs/${id}`),
  createFetchConfig: (data) => api.post('/fetch/configs', data),
  deleteFetchConfig: (id) => api.delete(`/fetch/configs/${id}`),
  getDataTypes: () => api.get('/fetch/data-types'),
  previewFetch: (data) => api.post('/fetch/preview', data),

  // Sync States
  getSyncStates: () => api.get('/sync/states'),
  getSyncState: (jobId) => api.get(`/sync/states/${jobId}`),

  // Transformations
  getTransformations: (jobId) => api.get('/transformations', { params: jobId ? { job_id: jobId } : {} }),
  getTransformation: (id) => api.get(`/transformations/${id}`),
  createTransformation: (data) => api.post('/transformations', data),
  updateTransformation: (id, data) => api.put(`/transformations/${id}`, data),
  deleteTransformation: (id) => api.delete(`/transformations/${id}`),
  previewTransformation: (data) => api.post('/transformations/preview', data),
  getTransformTemplates: () => api.get('/transformations/templates'),
  getTransformTemplate: (id) => api.get(`/transformations/templates/${id}`),
  applyTransformTemplate: (id, data) => api.post(`/transformations/templates/${id}/apply`, data),
};

export default api;
